import { Box, Button, Checkbox, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useState } from "react";
import YoYChip from "../YoYChip";

// Quarter-by-quarter table for the drill-down pages' "Table" view. Rows are
// the page's KPI-card categories; columns are Q1..Q4 of the selected year
// plus a full-year total. Ignores the page's quarter filter — the quarters
// ARE the columns. Mirrors the footprint page's QuarterlyEmissionsTable
// (variance chips, Compare checkbox, qoq/yoy toggle) but takes its rows as
// data so each scope page can map its own payload fields.
//
// Props:
// - year
// - rows: [{ label, bold?, indent?, values: [q1,q2,q3,q4,year], prevValues: [...] }]
//   `values`/`prevValues` entries may be null/undefined for missing periods.
// - isLoading
// - accent: header band colour (matches the page's accent)
// - unitLabel: unit named in the footer note (default "tCO2e" — every existing
//   caller is a scope/emissions page; pass e.g. "kWh" for a consumption table)

const PERIOD_LABELS = ["Q1", "Q2", "Q3", "Q4", "Year"];

const COMPARE_OPTIONS = [
  { label: "vs previous quarter", value: "qoq" },
  { label: "vs same quarter last year", value: "yoy" },
];

const formatCell = (value) =>
  typeof value === "number" && value !== 0
    ? value.toLocaleString("en-GB", { maximumFractionDigits: 2 })
    : "–";

const pctChange = (current, previous) => {
  if (typeof current !== "number" || current === 0) return null;
  if (typeof previous !== "number" || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const varianceFor = (values, prevValues, i, compareMode) => {
  if (compareMode === "qoq") {
    if (i === 0 || i === 4) return null;
    return pctChange(values[i], values[i - 1]);
  }
  return pctChange(values[i], prevValues?.[i]);
};

const CompareToggle = ({ mode, setMode }) => (
  <Flex bg="gray.100" borderRadius="full" p="3px">
    {COMPARE_OPTIONS.map((option) => {
      const active = mode === option.value;
      return (
        <Button
          key={option.value}
          size="xs"
          borderRadius="full"
          bg={active ? "pureBlack" : "transparent"}
          color={active ? "white" : "gray.500"}
          fontWeight={active ? "600" : "normal"}
          onClick={() => setMode(option.value)}
          _hover={{ bg: active ? "pureBlack" : "gray.200" }}
          px={3}
        >
          {option.label}
        </Button>
      );
    })}
  </Flex>
);

// Number first, chip to its right in a fixed slot so a column's numbers all
// end at the same x (see QuarterlyEmissionsTable).
const CellValue = ({ value, variance, bold, showSlot }) => (
  <Flex justify="flex-end" align="center" gap={2}>
    <Text fontWeight={bold ? "600" : "inherit"}>{formatCell(value)}</Text>
    {showSlot && (
      <Flex w="76px" justify="flex-start" flexShrink={0}>
        {variance != null && <YoYChip pctChange={variance} />}
      </Flex>
    )}
  </Flex>
);

// Called by parents to decide whether to fetch previous-year data.
export const useQuarterlyTableCompare = () => {
  const [showComparison, setShowComparison] = useState(false);
  const [compareMode, setCompareMode] = useState("qoq");
  return {
    showComparison,
    setShowComparison,
    compareMode,
    setCompareMode,
    needsPreviousYear: showComparison && compareMode === "yoy",
  };
};

const QuarterlyBreakdownTable = ({ year, rows, isLoading = false, compare, unitLabel = "tCO2e" }) => {
  const { showComparison, setShowComparison, compareMode, setCompareMode } = compare;
  const isYoY = showComparison && compareMode === "yoy";
  const visibleRows = (rows || []).filter((row) =>
    row.values.some((v) => typeof v === "number" && v !== 0)
  );

  return (
    <Box>
      <Flex align="center" gap={3} mb={2}>
        <Checkbox
          colorScheme="limeShade"
          size="sm"
          isChecked={showComparison}
          onChange={(e) => setShowComparison(e.target.checked)}
        >
          <Text fontSize="body.xs" color="gray.600">
            Compare
          </Text>
        </Checkbox>
        {showComparison && <CompareToggle mode={compareMode} setMode={setCompareMode} />}
      </Flex>
      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflowX="auto" bg="white">
        {isLoading ? (
          <Flex justify="center" py={10}>
            <Text fontSize="13px" color="gray.500">
              Loading quarterly data…
            </Text>
          </Flex>
        ) : visibleRows.length === 0 ? (
          <Flex justify="center" py={10}>
            <Text fontSize="13px" color="gray.500" px={6} textAlign="center">
              No quarterly data — emissions for this year were reported at the yearly level only.
            </Text>
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th color="gray.600" py={3}>
                  Category
                </Th>
                {PERIOD_LABELS.map((label) => (
                  <Th key={label} isNumeric color="gray.600" py={3}>
                    {/* Mirror CellValue's chip slot so the header label
                        right-aligns with the numbers, not the chip column. */}
                    <Flex justify="flex-end" align="center" gap={2}>
                      <Text as="span">{label === "Year" ? `${year} Total` : label}</Text>
                      {showComparison && <Box w="76px" flexShrink={0} />}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {visibleRows.map((row) => (
                <Tr key={row.label}>
                  <Td
                    pl={row.indent ? 8 : undefined}
                    fontSize={row.indent ? "body.xs" : undefined}
                    fontWeight={row.bold ? "600" : "normal"}
                    color={row.indent ? "gray.600" : "gray.700"}
                  >
                    {row.label}
                  </Td>
                  {row.values.map((v, i) => (
                    <Td
                      key={PERIOD_LABELS[i]}
                      isNumeric
                      fontSize={row.indent ? "body.xs" : undefined}
                      fontWeight={i === 4 ? "600" : "normal"}
                      color="gray.800"
                    >
                      <CellValue
                        value={v}
                        variance={showComparison ? varianceFor(row.values, row.prevValues, i, compareMode) : null}
                        bold={i === 4}
                        showSlot={showComparison}
                      />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        All figures in {unitLabel}. A dash means no report is available for that period; quarters may not
        sum exactly to the year total when data was entered at the yearly level.{" "}
        {showComparison &&
          (isYoY
            ? `Change chips compare each period with the same period of ${year - 1}; green = lower emissions.`
            : "Change chips compare each quarter with the one before it; green = lower emissions.")}
      </Text>
    </Box>
  );
};

export default QuarterlyBreakdownTable;
